import React from 'react';
import { cn } from '../lib/utils';

/**
 * SparksRing — wraps an avatar with a static gradient ring (Instagram/Facebook style).
 *
 * - Always a perfect circle (no rotation, no shape drift)
 * - Active = rainbow gradient, viewed = visible muted-grey ring, inactive = no ring
 *
 * The structure is two stacked, absolutely-positioned circles:
 *   ┌─ outer div (gradient background, perfectly round)
 *   │   └─ inner avatar (inset by `thickness + 2px` so the ring stays a clean width)
 *
 * No <button> wrapper — button user-agent styles can introduce corner radii.
 * Clickability is handled with `role="button"` + onClick on the wrapper div.
 */
export default function SparksRing({
  hasActive = false,
  viewed = false,
  size = 56,
  thickness = 2,
  className = '',
  onClick,
  children,
  testId,
}) {
  // Inactive — render the avatar in a hard-locked circle wrapper.
  if (!hasActive) {
    return (
      <div
        className={cn('shrink-0 overflow-hidden', className)}
        style={{ width: size, height: size, borderRadius: '50%' }}
        data-testid={testId || 'sparks-ring-inactive'}
      >
        {children}
      </div>
    );
  }

  // Active — thin solid ring (red), viewed — muted grey
  const ringBackground = viewed ? '#4b5563' : '#dc2626';

  const insetPx = Math.min(thickness, 2); // visible ring width, capped at 2px

  return (
    <div
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') onClick(e); } : undefined}
      data-testid={testId || 'sparks-ring'}
      data-spark-active="true"
      data-spark-viewed={viewed ? 'true' : 'false'}
      className={cn(
        'relative shrink-0',
        onClick && 'cursor-pointer hover:scale-[1.04] active:scale-95 transition-transform',
        className
      )}
      style={{
        width: size,
        height: size,
        background: ringBackground,
        borderRadius: '50%',
      }}
      aria-label={onClick ? 'View sparks' : undefined}
    >
      {/* Inner avatar — absolutely positioned with insets so the gradient remains a clean ring */}
      <div
        className="absolute overflow-hidden"
        style={{
          top: insetPx,
          left: insetPx,
          right: insetPx,
          bottom: insetPx,
          borderRadius: '50%',
          backgroundColor: 'var(--sparks-ring-bg, #0f0f14)',
        }}
      >
        {children}
      </div>
    </div>
  );
}
